import React, { useEffect, useMemo, useState } from 'react';

import { calculateRowTotals } from '../../utils/calculations';
import { getIndentClass } from '../../utils/styleUtils';

import CommittedSection from './CommittedSection';
import RevenueMilestoneData from './RevenueMilestoneData';
import UncommittedSection from './UncommittedSection';

import { formatFigure } from 'Helpers/formatService';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronUpIcon from 'Assets/icons/chevron-up.svg';

export default function RevenueRow(props) {
    const [milestonesCount, setMilestonesCount] = useState(null);
    const [showChildren, setShowChildren] = useState(false);

    // Handle expand all trigger from parent with staggered updates
    useEffect(() => {
        const t = props.expandAllTrigger;
        if (!t?.version) return;

        if (t.batch === 'rows') {
            setShowChildren(t.expanded);
        }
    }, [props.expandAllTrigger?.version]);

    // Calculate indentation level
    const indentLevel = props.indentLevel;
    const childIndentLevel = indentLevel + 1;

    useEffect(() => {
        setMilestonesCount(props.revenue.currentMilestones.length);
    }, [props.revenue]);

    const toggleShow = () => {
        setShowChildren(!showChildren);
    };

    // Calculate total periods count from the periods structure
    const periodsKeys = Object.keys(props.revenue.uncommitted?.periods || {});
    const maxRevenueMilestonesCount = Math.max(props.maxRevenueMilestonesCount, periodsKeys.length);

    const totals = useMemo(() => {
        return calculateRowTotals(props.revenue.currentMilestones);
    }, [props.revenue.currentMilestones]);

    const hasUncommittedData = useMemo(() => {
        return (
            props.revenue.uncommitted &&
            (props.revenue.uncommitted.value > 0 || (props.revenue.uncommitted.milestones && props.revenue.uncommitted.milestones.length > 0))
        );
    }, [props.revenue.uncommitted]);

    const hasCommittedData = useMemo(() => {
        const committed = props.revenue.committed;
        if (!committed) return false;

        if (Array.isArray(committed)) {
            return committed.length > 0 && committed.some((contract) => contract.currentMilestones && contract.currentMilestones.length > 0);
        } else if (typeof committed === 'object') {
            const contracts = Object.values(committed);
            return contracts.length > 0 && contracts.some((contract) => contract.currentMilestones && contract.currentMilestones.length > 0);
        }

        return false;
    }, [props.revenue.committed]);

    return (
        <>
            {milestonesCount !== null && (
                <tr className="!tw-bg-blue-25">
                    <td className="tw-font-bold tw-cursor-pointer !tw-border-r-0">
                        <div className={`tw-flex tw-items-center tw-gap-x-1.5 tw-cursor-pointer ${getIndentClass(indentLevel)}`} onClick={toggleShow}>
                            <div className="!tw-text-blue">{props.revenue.name}</div>
                            <div>{showChildren ? <ChevronUpIcon /> : <ChevronDownIcon />}</div>
                        </div>
                    </td>
                    <td className="tw-p-4"></td>
                    <td className="tw-p-4 tw-text-right tw-border-r-2">{formatFigure(props.revenue.value)}</td>
                    {props.revenue.currentMilestones &&
                        [...Array(maxRevenueMilestonesCount)].map((e, i) => {
                            return (
                                <React.Fragment key={`th_milestones_${i}_date_percentage`}>
                                    <RevenueMilestoneData
                                        key={`${props.keyPrefix || ''}tr_revenue_${props.revenue.id}_date_field_${i}`}
                                        milestones={props.revenue.currentMilestones}
                                        milestoneId={i}
                                        keyPrefix={props.keyPrefix || ''}
                                        amountOnly={true}
                                    />
                                </React.Fragment>
                            );
                        })}
                    <td className="tw-p-4 tw-text-right"></td>
                    <td className="tw-p-4 tw-text-right">{formatFigure(totals.totalValue)}</td>
                    <td className="tw-p-4 tw-text-center"></td>
                </tr>
            )}
            {showChildren && (
                <>
                    {hasUncommittedData && (
                        <UncommittedSection
                            {...props}
                            key={`uncommitted_revenue_${props.revenue.id}`}
                            revenue={props.revenue}
                            periods={props.periods}
                            maxRevenueMilestonesCount={maxRevenueMilestonesCount}
                            indentLevel={childIndentLevel}
                        />
                    )}

                    {hasCommittedData && (
                        <CommittedSection
                            {...props}
                            key={`committed_revenue_${props.revenue.id}`}
                            revenue={props.revenue}
                            periods={props.periods}
                            maxRevenueMilestonesCount={maxRevenueMilestonesCount}
                            indentLevel={childIndentLevel}
                        />
                    )}
                </>
            )}
        </>
    );
}
